import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { APP_ROUTES } from '@/navigation';
import '@/learn.css';

const VOICING_LAYERS = [
  {
    label: 'Chord quality',
    example: 'Cmaj7',
    description: 'Chooses the four chord tones: C, E, G and B.',
  },
  {
    label: 'Voicing family',
    example: 'Drop 3',
    description: 'Chooses which voices move into another octave.',
  },
  {
    label: 'Top voice',
    example: '7th (B) on top',
    description: 'Rotates the close-position chord so the chosen tone is on top.',
  },
  {
    label: 'String layout',
    example: 'Lower register',
    description: 'Places the four voices on a group of guitar strings.',
  },
  {
    label: 'Fingering',
    example: 'Frets 12–10–12–12',
    description: 'Places the voicing at one position on the neck.',
  },
] as const;

const FAMILY_COMPARISON = [
  {
    name: 'Close',
    operation: 'No voices dropped',
    notes: 'C · E · G · B',
    result: 'Root position · root in bass',
    character: 'Compact, but often hard to play on adjacent strings.',
  },
  {
    name: 'Drop 2',
    operation: 'G moves down',
    notes: 'G · C · E · B',
    result: '2nd inversion · 5th in bass',
    character: 'Even spacing on four adjacent strings.',
  },
  {
    name: 'Drop 3',
    operation: 'E moves down',
    notes: 'E · C · G · B',
    result: '1st inversion · 3rd in bass',
    character: 'A low bass note below three close upper voices.',
  },
  {
    name: 'Drop 2 & 4',
    operation: 'G and C move down',
    notes: 'C · G · E · B',
    result: 'Root position · root in bass',
    character: 'A wide spread over two octaves.',
  },
] as const;

export function FourNoteVoicingsPage() {
  return (
    <article className="voicing-primer-page">
      <nav className="learn-breadcrumb" aria-label="Breadcrumb">
        <Link to={APP_ROUTES.learn}>Learn</Link>
        <span aria-hidden="true">/</span>
        <span aria-current="page">How four-note voicings work</span>
      </nav>

      <header className="drop2-hero">
        <p className="learn-eyebrow">Foundations</p>
        <h1>Four-note voicings</h1>
        <p>
          Drop 2 and drop 3 are voicing families, not chord qualities. A voicing family is a fixed
          way of spreading the same four notes across octaves before placing them on the guitar.
        </p>
      </header>

      <nav className="drop2-toc" aria-label="On this page">
        <a href="#layers">Five layers</a>
        <a href="#families">Compare families</a>
        <a href="#layouts">String layouts</a>
        <a href="#choose">Next steps</a>
      </nav>

      <section id="layers" className="drop2-section" aria-labelledby="voicing-layers-heading">
        <div className="drop2-section-heading">
          <span aria-hidden="true">01</span>
          <div>
            <h2 id="voicing-layers-heading">Five layers of a voicing</h2>
          </div>
        </div>
        <p className="drop2-lede">
          A voicing is set by five separate choices. Changing the family changes the spacing and can
          change the bass note. Changing the layout moves the same spacing to other strings. Neither
          changes the chord.
        </p>

        <ol className="voicing-layer-flow">
          {VOICING_LAYERS.map((layer, index) => (
            <li key={layer.label}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <div>
                <h3>{layer.label}</h3>
                <strong>{layer.example}</strong>
                <p>{layer.description}</p>
              </div>
            </li>
          ))}
        </ol>

        <aside className="drop2-principle">
          <strong>Top voice is not inversion</strong>
          <p>
            Dropping a voice does not change the top note, so B stays on top in every family. The
            inversion is named from the bass note after the drop: this Cmaj7 is a 2nd inversion in
            drop 2 and a 1st inversion in drop 3. The inversion of the close-position chord is not
            the inversion of the result.
          </p>
        </aside>
      </section>

      <section id="families" className="drop2-section" aria-labelledby="families-heading">
        <div className="drop2-section-heading">
          <span aria-hidden="true">02</span>
          <div>
            <h2 id="families-heading">Voicing families compared</h2>
          </div>
        </div>
        <p className="drop2-lede">
          Start from close-position Cmaj7, read low to high. Drop 3 means lowering the third voice
          from the top by an octave. It does not refer to the third of the chord or to the third
          string.
        </p>

        <div className="voicing-family-table-wrap">
          <table className="voicing-family-table">
            <thead>
              <tr>
                <th scope="col">Family</th>
                <th scope="col">Operation</th>
                <th scope="col">Low → high</th>
                <th scope="col">Finished inversion</th>
                <th scope="col">Character</th>
              </tr>
            </thead>
            <tbody>
              {FAMILY_COMPARISON.map((family) => (
                <tr key={family.name}>
                  <th scope="row">{family.name}</th>
                  <td>{family.operation}</td>
                  <td className="drop2-note-sequence">{family.notes}</td>
                  <td>{family.result}</td>
                  <td>{family.character}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <aside className="drop2-principle">
          <strong>What stays the same</strong>
          <p>
            Every row contains C, E, G and B, with B on top. Only the octaves change, so the chord
            and the top note stay the same. The bass note, and so the inversion, can change.
          </p>
        </aside>

        <aside className="drop2-principle">
          <strong>Rootless voicings</strong>
          <p>
            Inversion names assume the chord has a root. A rootless voicing is named by its bass and
            top notes instead, for example “♭7 in bass · 9th on top”.
          </p>
        </aside>
      </section>

      <section id="layouts" className="drop2-section" aria-labelledby="layouts-heading">
        <div className="drop2-section-heading">
          <span aria-hidden="true">03</span>
          <div>
            <h2 id="layouts-heading">String layouts</h2>
          </div>
        </div>
        <p className="drop2-lede">
          The family sets the spacing of the notes. The string layout sets which strings they are
          played on. Drop 2 usually uses four adjacent strings. Drop 3 usually skips the string
          above the bass note.
        </p>

        <div className="voicing-layout-comparison">
          <article>
            <p className="learn-eyebrow">Drop 2 · adjacent</p>
            <div className="voicing-string-strip" aria-label="Drop 2 on A, D, G and B strings">
              <span>E</span>
              <strong>A</strong>
              <strong>D</strong>
              <strong>G</strong>
              <strong>B</strong>
              <span>e</span>
            </div>
          </article>
          <article>
            <p className="learn-eyebrow">Drop 3 · skipped</p>
            <div
              className="voicing-string-strip"
              aria-label="Drop 3 on low E, D, G and B strings, skipping A"
            >
              <strong>E</strong>
              <span className="is-skip">×</span>
              <strong>D</strong>
              <strong>G</strong>
              <strong>B</strong>
              <span>e</span>
            </div>
          </article>
        </div>
      </section>

      <footer id="choose" className="drop2-next">
        <h2>Next steps</h2>
        <p>Learn one family at a time, then use it in the Chord library or the workbench.</p>
        <div className="drop2-next-grid">
          <Link to={APP_ROUTES.drop2Intro}>
            <span>Voicing family</span>
            <strong>Learn Drop 2</strong>
            <ArrowRight size={17} aria-hidden="true" />
          </Link>
          <Link to={APP_ROUTES.drop3Intro}>
            <span>Voicing family</span>
            <strong>Learn Drop 3</strong>
            <ArrowRight size={17} aria-hidden="true" />
          </Link>
          <Link to={`${APP_ROUTES.lab}/v-system`}>
            <span>Explore</span>
            <strong>See every four-note spread</strong>
            <ArrowRight size={17} aria-hidden="true" />
          </Link>
          <Link to={APP_ROUTES.chordLibrary}>
            <span>Reference</span>
            <strong>Open the Chord library</strong>
            <ArrowRight size={17} aria-hidden="true" />
          </Link>
        </div>
      </footer>
    </article>
  );
}
